import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { getMyAccess, mintEmbedUrl } from "../api/reports";
import { ApiError } from "../api/client";
import type {
  AccessMatrix,
  EmbedURLResponse,
  PageWithAccess,
  TenantBranding,
} from "../types";
import type { MeResponse } from "../api/me";
import { VentureTopBar } from "../components/Chrome";

// E3 — venture page view. Server mints a signed embed URL after checking
// access; we render it in an iframe. If the embed mint returns 403 we
// redirect to the E4 denied screen — the backend is authoritative.
export function VenturePage({
  branding,
  tenantSlug,
  me,
  pageId,
  isAdmin = false,
}: {
  branding: TenantBranding;
  tenantSlug: string;
  me: MeResponse;
  pageId: number;
  isAdmin?: boolean;
}) {
  const [matrix, setMatrix] = useState<AccessMatrix | null>(null);
  const [embed, setEmbed] = useState<EmbedURLResponse | null>(null);
  const [error, setError] = useState<{ status: number; detail: string } | null>(
    null,
  );

  useEffect(() => {
    setEmbed(null);
    setError(null);
    void (async () => {
      try {
        const [m, e] = await Promise.all([
          getMyAccess(tenantSlug),
          mintEmbedUrl(tenantSlug, pageId),
        ]);
        setMatrix(m);
        setEmbed(e);
      } catch (err) {
        if (err instanceof ApiError) {
          setError({ status: err.status, detail: err.detail });
        } else {
          setError({ status: 500, detail: (err as Error).message });
        }
      }
    })();
  }, [tenantSlug, pageId]);

  // 403 on the embed mint = no access (the server is the security gate).
  // Redirect to the E4 denied screen so the user has a stable URL.
  if (error?.status === 403) {
    return <Navigate to="/denied" replace />;
  }

  const grantedPages: PageWithAccess[] = (matrix?.pages ?? []).filter(
    (p) => p.granted,
  );

  return (
    <div className="venture">
      <VentureTopBar
        branding={branding}
        me={me}
        grantedPages={grantedPages}
        activePageId={pageId}
        isAdmin={isAdmin}
      />
      <div className="venture-body">
        {error && error.status !== 403 && (
          <div className="empty err">
            Couldn't load page: {error.detail}
          </div>
        )}
        {embed === null && !error && <div className="empty">Loading…</div>}
        {embed && (
          <div className="embed-wrap">
            <h1 className="pg-title" style={{ marginBottom: 16 }}>
              {embed.page_name}
            </h1>
            <iframe
              src={embed.url}
              title={embed.page_name}
              className="embed-frame"
              allowTransparency
              frameBorder={0}
            />
          </div>
        )}
      </div>
    </div>
  );
}
